import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SectionHead, Chip } from '@/components/aurora'
import { User, Calendar, ArrowRight } from 'lucide-react'
import { getFollowingTeachers, getUpcomingExams } from '@/api/social'

function Card({ children }) {
  return (
    <div className="rounded-2xl border px-5 py-5" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      {children}
    </div>
  )
}

function Row({ children }) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      {children}
    </div>
  )
}

export function LinkedTeachers() {
  const { t } = useTranslation()
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    getFollowingTeachers({ signal: controller.signal })
      .then(({ data }) => setTeachers(data.results ?? data ?? []))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setTeachers([]) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [])

  return (
    <Card>
      <SectionHead
        title={t('dashboard.linkedTeachers', "O'qituvchilar")}
        more={
          <a
            href="/teachers"
            className="inline-flex items-center gap-1 text-[12px] transition-colors"
            style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}
          >
            {t('dashboard.seeAll', "Hammasi")} <ArrowRight size={12}/>
          </a>
        }
      />
      <div className="flex flex-col gap-2 mt-2">
        {!loading && teachers.length === 0 && (
          <p className="text-[12.5px] m-0" style={{ color: 'var(--ink-3)' }}>
            {t('dashboard.noFollowedTeachers', "Hali hech kimni kuzatmayapsiz")}
          </p>
        )}
        {teachers.map((teacher) => (
          <Row key={teacher.id}>
            <span
              className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0 border"
              style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)', color: 'var(--coral)' }}
            >
              <User size={16} strokeWidth={2}/>
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[13.5px] truncate m-0" style={{ color: 'var(--ink)' }}>{teacher.full_name}</p>
              <p className="font-mono text-[10.5px] m-0 mt-0.5" style={{ color: 'var(--ink-3)' }}>@{teacher.username}</p>
            </div>
            <Chip variant="dim" className="text-[10px]">{teacher.exams_count} {t('teachers.exams', 'imtihon')}</Chip>
          </Row>
        ))}
      </div>
    </Card>
  )
}

export function UpcomingEvents() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [exams, setExams] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    getUpcomingExams({ signal: controller.signal })
      .then(({ data }) => setExams(data ?? []))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setExams([]) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [])

  return (
    <Card>
      <SectionHead title={t('dashboard.upcomingEvents', 'Tez orada')} />
      <div className="flex flex-col gap-2 mt-2">
        {!loading && exams.length === 0 && (
          <p className="text-[12.5px] m-0" style={{ color: 'var(--ink-3)' }}>
            {t('dashboard.noUpcomingExams', "Kuzatayotgan o'qituvchilaringizda rejalashtirilgan imtihon yo'q")}
          </p>
        )}
        {exams.map((ex) => {
          const dt = ex.start_time ? new Date(ex.start_time) : null
          return (
            <button
              key={ex.id}
              onClick={() => navigate(`/exam/${ex.code}`)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
            >
              <span className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: 'var(--coral)' }}/>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[13.5px] m-0 truncate" style={{ color: 'var(--ink)' }}>{ex.title}</p>
                <p className="text-[10.5px] m-0 mt-0.5 truncate" style={{ color: 'var(--ink-3)' }}>{ex.teacher_name}</p>
              </div>
              {dt && (
                <div className="flex items-center gap-1.5 font-mono text-[10.5px] flex-shrink-0" style={{ color: 'var(--ink-3)' }}>
                  <Calendar size={10} strokeWidth={2}/> {dt.toLocaleDateString()} · {dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </Card>
  )
}

export default LinkedTeachers
